import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { ApiError } from "../../api/client";
import { useMe } from "../../auth/useMe";
import { RacesApi, type Race, type RaceScopeLevel } from "./api";

/**
 * Races admin: elected positions being contested (Councillor, Mayor, MP,
 * President). Distinct from `positions`, which are org-chart anchors used
 * for RBAC scope.
 */
export function Races() {
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const [error, setError] = useState<string | null>(null);

  const races = useQuery({ queryKey: ["races"], queryFn: RacesApi.list });

  const create = useMutation({
    mutationFn: RacesApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["races"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });
  const remove = useMutation({
    mutationFn: RacesApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["races"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [scopeLevel, setScopeLevel] = useState<RaceScopeLevel>("constituency");
  const [order, setOrder] = useState("100");

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate({
      name: name.trim(),
      description: desc.trim(),
      scopeLevel,
      order: Number(order) || 100,
    });
    setName(""); setDesc(""); setOrder("100");
  }

  return (
    <div className="hierarchy">
      <header>
        <h1>Races</h1>
        <p className="muted">
          A <strong>race</strong> is an elected seat voters choose someone to fill — Councillor, Mayor, MP, President.
          Voter intelligence captures can be tagged with a race so projections roll up per contest.
        </p>
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Scope tells us at what geographic level the race happens. Order controls the display sequence (low first).
        </p>
      </header>

      {error && <div role="alert" className="error">{error}</div>}

      <form className="create" onSubmit={submit}>
        <input
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Race name (e.g. Mayor)"
          autoCapitalize="words"
        />
        <input
          value={desc} onChange={(e) => setDesc(e.target.value)}
          placeholder="Description"
        />
        <select value={scopeLevel} onChange={(e) => setScopeLevel(e.target.value as RaceScopeLevel)}>
          <option value="national">National</option>
          <option value="district">District</option>
          <option value="constituency">Constituency</option>
          <option value="ward">Ward</option>
        </select>
        <input
          value={order} onChange={(e) => setOrder(e.target.value)}
          placeholder="Order"
          style={{ maxWidth: 80 }}
          type="number"
        />
        <button type="submit" disabled={create.isPending}>{create.isPending ? "Adding…" : "Add"}</button>
      </form>

      <ul className="tree">
        {races.data?.items.map((r: Race) => (
          <li key={r.id}>
            <div className="row">
              <span>
                <strong>{r.name}</strong>{" "}
                <span className="muted">({r.scopeLevel} · order {r.order})</span>
                <div className="muted" style={{ fontSize: 12 }}>{r.description || "—"}</div>
              </span>
              {isSuperadmin && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Delete "${r.name}"? Refused if any voter intel records reference it.`))
                      remove.mutate(r.id);
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </li>
        ))}
        {races.data?.items.length === 0 && !races.isLoading && (
          <li className="muted">No races yet. Add one above.</li>
        )}
      </ul>
    </div>
  );
}
